<script setup>
import { getCurrentInstance } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { useLoading } from '@/composables/useLoading'
import Dropdown from 'primevue/dropdown'

const { proxy } = getCurrentInstance()
const $t = proxy.$t
const { showLoading, hideLoading } = useLoading()

const props = defineProps({
    user: Object,
})

const form = useForm({
    _method: 'PUT',
    first_name: props.user?.first_name || '',
    last_name: props.user?.last_name || '',
    name: props.user?.name || '',
    birthday: props.user?.birthday || '',
    email: props.user?.email || '',
    photo: null,
    phone: props.user?.phone || '',
    contact: props.user?.contact || '',
    contact_where: props.user?.contact_where || '',
    contact_id: props.user?.contact_id || '',
    contact_how: props.user?.contact_how || '',
    notify_updates_via_email: props.user?.notify_updates_via_email || 'DISABLED',
})

const contactOptions = [
    { value: 'email', label: 'Email' },
    { value: 'call', label: 'Phone call' },
    { value: 'message', label: 'Message app' },
    { value: 'other', label: 'Other' },
]

const notificationOptions = [
    { value: 'MINIMAL', label: 'Minimal' },
    { value: 'ALL', label: 'All' },
    { value: 'DISABLED', label: 'Disabled' },
]

const updateProfileInformation = () => {
    showLoading($t('Updating contact information...'));
    const routeName = proxy.route ? proxy.route('client.profile-information.update') : '/profile-information'
    form.post(routeName, {
        errorBag: 'updateProfileInformation',
        preserveScroll: true,
        onFinish: () => hideLoading(),
    })
}
</script>

<template>
    <form @submit.prevent="updateProfileInformation" class="space-y-6">
        <!-- Phone -->
        <div class="field">
            <label for="phone" class="block text-sm font-medium text-surface-700 mb-2">
                {{ $t('Phone') }}
            </label>
            <InputText
                id="phone"
                v-model="form.phone"
                type="tel"
                class="w-full"
                :class="{ 'p-invalid': form.errors.phone }"
                autocomplete="tel"
            />
            <small v-if="form.errors.phone" class="p-error">
                {{ form.errors.phone }}
            </small>
        </div>

        <!-- Contact Preference -->
        <div class="field">
            <label class="block text-sm font-medium text-surface-700 mb-4">
                {{ $t('Best way to contact me is...') }}
            </label>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div
                    v-for="option in contactOptions"
                    :key="option.value"
                    class="flex items-center"
                >
                    <RadioButton
                        :id="`contact_${option.value}`"
                        v-model="form.contact"
                        :value="option.value"
                        name="contact"
                    />
                    <label
                        :for="`contact_${option.value}`"
                        class="ml-2 text-sm text-surface-700 cursor-pointer"
                    >
                        {{ $t(option.label) }}
                    </label>
                </div>
            </div>
            <small v-if="form.errors.contact" class="p-error">
                {{ form.errors.contact }}
            </small>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Contact Where -->
            <div class="field">
                <label for="contact_where" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Which one') }}
                </label>
                <InputText
                    id="contact_where"
                    v-model="form.contact_where"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.contact_where }"
                    :disabled="form.contact !== 'other'"
                    :placeholder="form.contact !== 'other' ? $t('Only for Other option') : $t('Specify the platform or method')"
                />
                <small v-if="form.errors.contact_where" class="p-error">
                    {{ form.errors.contact_where }}
                </small>
            </div>

            <!-- Contact ID -->
            <div class="field">
                <label for="contact_id" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('How we find you there? (ID, username, @, number, etc)') }}
                </label>
                <InputText
                    id="contact_id"
                    v-model="form.contact_id"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.contact_id }"
                />
                <small v-if="form.errors.contact_id" class="p-error">
                    {{ form.errors.contact_id }}
                </small>
            </div>
        </div>

        <!-- Contact How -->
        <div class="field">
            <label for="contact_how" class="block text-sm font-medium text-surface-700 mb-2">
                {{ $t('Tell us how') }}
            </label>
            <Textarea
                id="contact_how"
                v-model="form.contact_how"
                rows="4"
                class="w-full"
                :class="{ 'p-invalid': form.errors.contact_how }"
                :placeholder="$t('Describe the best way and time to contact you...')"
            />
            <small v-if="form.errors.contact_how" class="p-error">
                {{ form.errors.contact_how }}
            </small>
        </div>
        <div class="field">
            <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Email notifications') }}</label>
            <Dropdown
                v-model="form.notify_updates_via_email"
                :options="notificationOptions"
                optionLabel="label"
                optionValue="value"
                class="w-full"
                :placeholder="$t('Select notification frequency')"
            />
            <small v-if="form.errors.notify_updates_via_email" class="p-error">{{ form.errors.notify_updates_via_email }}</small>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-4 pt-4">
            <Button
                type="submit"
                :label="form.processing ? $t('Saving...') : $t('Save')"
                icon="pi pi-save"
                :loading="form.processing"
                :disabled="form.processing"
            />

            <div v-if="form.recentlySuccessful" class="flex items-center gap-2 text-green-600">
                <i class="pi pi-check-circle"></i>
                <span class="text-sm font-medium">{{ $t('Saved.') }}</span>
            </div>
        </div>
    </form>
</template>

<style scoped>
.field {
    @apply flex flex-col;
}

.p-error {
    @apply text-red-500 text-xs mt-1;
}

.p-invalid {
    @apply border-red-500;
}
</style>